import { z } from 'zod';

const error = z.string();

const create = z.null();
const login = z.null();
const sync = z.null();
const download = z.any();

const syncPlaylists = z.object({
    // The one file to fetch from /sync/download — either the tracks zip (with the
    // Rekordbox XML inside it, when asked for) or the XML on its own. Optional
    // because a pymix that predates it doesn't send it; callers fall back to
    // deriving the name from zipPath.
    downloadFilename: z.string().nullish(),
    nTracksExported: z.number(),
    reason: z.string(),
    success: z.boolean(),
    xmlIncluded: z.boolean().nullish(),
    // Null for a metadata-only export: there is no zip in that case.
    zipPath: z.string().nullish(),
});

const matchTracks = z.object({
    reason: z.string(),
    success: z.boolean(),
    tracks: z.array(
        z.object({
            artist: z.string(),
            matched: z.boolean(),
            title: z.string(),
        }),
    ),
});

const isValidToken = z.object({
    is_valid_token: z.boolean(),
    reason: z.string(),
    success: z.boolean(),
});

const deleteDuplicates = z.object({
    duplicates_removed: z.array(z.string()),
    reason: z.string(),
    success: z.boolean(),
});

const deleteSongResult = z.object({
    reason: z.string(),
    subbox_id: z.string(),
    success: z.boolean(),
});

const deleteSong = z.object({
    results: z.array(deleteSongResult),
    success: z.boolean(),
    // The deleted tracks went to this trash batch (pymix#200): restorable until it's
    // purged. Absent from an older pymix, null when nothing was deleted.
    trash_batch_id: z.string().nullish(),
    username: z.string(),
});

const importJob = z.object({
    job_id: z.string(),
    max_library_size_exceeded: z.boolean(),
    n_tracks_for_import: z.number(),
    reason: z.string(),
    success: z.boolean(),
});

// What one pass of a job attempted, and how it went (pymix migration 019). The
// counts are the work itself; `reason` and `warnings` below are prose *about* it
// and can only ever describe one pass.
const jobPhase = z.object({
    failed: z.number(),
    ok: z.number(),
    phase: z.string(),
    skipped: z.number(),
    total: z.number(),
});

const beetsImportProgress = z.object({
    in_progress: z.boolean(),
    n_tracks_processed: z.number(),
    n_tracks_to_process: z.number(),
    percentage_complete: z.number(),
    // Which of the import's three passes the server is on, and that pass's own
    // n/total (laker-93/pymix#51). Optional so an older pymix — or a job row
    // created before its migration — still parses.
    phase: z.string().nullish(),
    phase_n_processed: z.number().optional(),
    phase_n_total: z.number().optional(),
    // Written once, when the job finishes, so it is empty for the whole run — and
    // empty on a server that predates it, and on a job completed without a ledger.
    // So absent means "not reported", never "the job did nothing".
    phases: z.array(jobPhase).nullish(),
    reason: z.string(),
    result: z.boolean(),
    // The trash batch that undoes the playlists a re-import replaced (pymix#208),
    // once the job has finished. Null when it replaced none; absent from an older pymix.
    trash_batch_id: z.string().nullish(),
    // Set on a job that *succeeded* but did not do everything asked of it — a
    // Serato import whose crates named tracks that are not in the library. `reason`
    // only reaches the client on a failed job, so a partial success routed through
    // it would say nothing at all.
    warnings: z.string().nullish(),
});

const librarySize = z.object({
    reason: z.string(),
    success: z.boolean(),
    total_size_bytes: z.number(),
});

const exportJob = z.object({
    beets_output: z.string(),
    n_beets_tracks: z.number(),
    reason: z.string(),
    success: z.boolean(),
});

const storageCheck = z.object({
    allowed: z.boolean(),
    currentUsageBytes: z.number(),
    // The usage split (pymix#200): deleted tracks keep counting until the trash is
    // emptied. Absent from an older pymix.
    libraryBytes: z.number().optional(),
    maxStorageBytes: z.number(),
    reason: z.string(),
    remainingBytes: z.number(),
    success: z.boolean(),
    trashBytes: z.number().optional(),
});

const syncPlanTrackMissing = z.object({
    album: z.string().optional(),
    artist: z.string(),
    duration: z.number().optional(),
    fileSize: z.number().optional(),
    title: z.string(),
});

const syncPlanTrackExisting = z.object({
    album: z.string().optional(),
    artist: z.string(),
    status: z.string(),
    title: z.string(),
});

const syncPlanTrackConflict = z.object({
    album: z.string().optional(),
    artist: z.string(),
    reason: z.string().optional(),
    status: z.string(),
    title: z.string(),
});

const syncPlanMetadataUpdate = z.object({
    artist: z.string(),
    // Optional because pymix does not send it: /sync/plan builds each update from
    // the missing track's title and artist alone and never says which fields would
    // change. This contract isn't validated at runtime (see pymixApiClient — the
    // axios body is passed straight through), so declaring it required didn't make
    // it appear; it just let the renderer call .map() on undefined and crash the
    // Metadata Updates tab. Render it defensively until pymix computes real fields.
    fields: z.array(z.string()).optional(),
    title: z.string(),
});

const syncPlan = z.object({
    download: z.object({
        strategy: z.string(),
    }),
    metadata: z.object({
        updates: z.array(syncPlanMetadataUpdate),
    }),
    summary: z.object({
        downloadSizeBytes: z.number(),
        metadataUpdates: z.number(),
        playlists: z.number(),
        tracksAlreadyPresent: z.number(),
        tracksMissing: z.number(),
        tracksRequested: z.number(),
    }),
    tracks: z.object({
        conflicts: z.array(syncPlanTrackConflict),
        existing: z.array(syncPlanTrackExisting),
        missing: z.array(syncPlanTrackMissing),
    }),
});

// --- Parameter schemas ---

const createParameters = z.object({
    email: z.string(),
    password: z.string(),
    token: z.string(),
    username: z.string(),
});

const loginParameters = z.object({
    password: z.string(),
    username: z.string(),
});

const track = z.object({
    album: z.string().optional(),
    // A download's local track in the app's own music folder: pymix writes this path
    // as its Rekordbox XML Location, whatever name it would give the file today (#261).
    appLocation: z.string().optional(),
    artist: z.string(),
    fileExtension: z.string().optional(),
    fromTag: z.boolean().default(true),
    // SUBBOX_ID read off the local file's tags, when present. Lets sync/plan match
    // this track exactly instead of falling back to fuzzy title/artist matching.
    subboxId: z.string().optional(),
    title: z.string(),
    // A download's local track found where the user uploaded it from (#214): pymix
    // writes this path as its Rekordbox XML Location.
    userLocation: z.string().optional(),
});

const rbExportParameters = z.object({
    // The tracks the caller already has, so their Locations are where they are (#261).
    localTracks: z.array(track).optional(),
    playlistIds: z.array(z.string()).optional(),
    user_root: z.string(),
});

const syncParameters = z.object({
    tracks: z.array(track),
});

const matchTracksParameters = z.object({
    tracks: z.array(track),
});

const rbImportParameters = z.object({
    playlistNames: z.array(z.array(z.string())).nullable(),
    // The name the XML was uploaded under. pymix imports exactly that file; without
    // it, it takes the newest XML in uploads/, which may be a leftover (pymix#192).
    xmlName: z.string().optional(),
});

/**
 * Which subbox track each crate entry refers to.
 *
 * A `.crate` file stores an absolute path on the user's machine and nothing else,
 * and pymix never sees their files — so the client reads SUBBOX_ID off each one
 * and sends the mapping. `crate_path` must be the path exactly as the crate
 * stores it, because that is the key pymix looks the entry up by.
 */
const seratoCue = z.object({
    end_ms: z.number().nullish(),
    index: z.number(),
    name: z.string(),
    start_ms: z.number(),
    type: z.enum(['cue', 'loop']),
});

/**
 * One beat-grid anchor. Serato-shaped in both directions: every anchor but the
 * last carries `beats_till_next`, the last carries `bpm`. pymix converts a
 * Rekordbox-sourced grid — where every anchor has its own tempo — before
 * sending, so this side never does that arithmetic.
 */
const seratoBeatgridMarker = z.object({
    beats_till_next: z.number().nullish(),
    bpm: z.number().nullish(),
    position_ms: z.number(),
});

const seratoImportParameters = z.object({
    track_identities: z.array(
        z.object({
            /**
             * The cues as read off the user's own file. pymix can only read its
             * copy, which is frozen at whatever was uploaded — so for a track the
             * library already has, every cue set in Serato since is invisible to
             * it. Absent means "we couldn't read them, use your copy"; an empty
             * array means "we read them and there are none", which pymix leaves
             * alone rather than treating as a deletion.
             */
            /**
             * The beat grid, read the same three ways `cues` is. One wrinkle of
             * its own: a file Serato has analysed but not gridded carries the
             * frame with zero anchors in it, so an empty array is what a
             * *present* frame decodes to as often as it is what a missing
             * encoder produces. Frame presence is not evidence of a grid.
             */
            beatgrid: z.array(seratoBeatgridMarker).optional(),
            crate_path: z.string(),
            cues: z.array(seratoCue).optional(),
            subbox_id: z.string(),
        }),
    ),
});

/**
 * The playlists to write as Serato crates, and what goes in them.
 *
 * pymix used to write the `.crate` files itself, against a `user_root` this
 * client sent it — a prediction about a filesystem the server has never seen,
 * and a wrong one produces crates that parse perfectly and resolve nothing. So
 * it returns the structure and the main process writes the files against the
 * paths the download actually landed on.
 */
const seratoExportParameters = z.object({
    /** Empty for every playlist. */
    playlistIds: z.array(z.string()),
});

const seratoExport = z.object({
    crates: z.array(
        z.object({
            display_name: z.string(),
            /** Root first. One `.crate` file per level — that is how Serato spells a folder. */
            path_components: z.array(z.string()),
            tracks: z.array(
                z.object({
                    album: z.string(),
                    artist: z.string(),
                    beatgrid: z.array(seratoBeatgridMarker),
                    /** What the conversion to Serato's shape could not carry —
                     *  a time signature, a beat-of-bar, a rounded span. To show,
                     *  not to act on. */
                    beatgrid_notes: z.array(z.string()),
                    cues: z.array(seratoCue),
                    rating: z.number(),
                    /** Inside the download, under `music/`. Join onto the music folder. */
                    relative_path: z.string(),
                    subbox_id: z.string().nullish(),
                    title: z.string(),
                }),
            ),
        }),
    ),
    n_crates: z.number(),
    n_tracks: z.number(),
    reason: z.string(),
    success: z.boolean(),
});

const importProgressParameters = z.object({
    job_id: z.string(),
    public: z.boolean(),
});

const deleteParameters = z.object({
    public: z.boolean(),
});

const deleteSongParameters = z.object({
    ids: z.array(z.string()),
    username: z.optional(z.string()),
});

const isValidTokenParameters = z.object({
    token: z.string(),
});

// Not a server parameter — pymix ignores it. Every user's download is the same url
// (/sync/download/music.zip), so a CDN in front of pymix will happily hand back a
// stale, or another session's, file: Cloudflare was caching it for 4h in prod
// (laker-93/pymix#119). A value that changes per request keeps this client correct
// whatever sits in front of, or ships in, the server.
const downloadParameters = z.object({
    cache_bust: z.string(),
});

/**
 * Beta-invite capture. The one pymix write that carries no session — the caller is a
 * prospective user with no account. `dj_software_other` is only meaningful alongside
 * `other`; the server drops it otherwise.
 */
const inviteRequestParameters = z.object({
    dj_software: z.enum(['rekordbox', 'serato', 'other']),
    dj_software_other: z.string().optional(),
    email: z.string(),
});

const inviteRequest = z.object({
    status: z.string(),
});

const storageCheckParameters = z.object({
    uploadSizeBytes: z.number(),
});

const syncPlanPlaylist = z.object({
    id: z.string(),
    source: z.string(),
});

const importParameters = z.object({
    public: z.boolean(),
});

const syncPlanLocalTrack = track;

const syncPlanParameters = z.object({
    direction: z.enum(['download', 'upload']),
    localTracks: z.array(syncPlanLocalTrack),
    options: z
        .object({
            fuzzyMatch: z.boolean().optional(),
            includeMetadata: z.boolean().optional(),
        })
        .optional(),
    playlists: z.array(syncPlanPlaylist).nullable(),
});

// What to put in the single file pymix prepares. The client always asks for one
// download: a browser only reliably saves one file per user gesture, so a second
// programmatic download is dropped with no error at all.
const syncPlaylistsParameters = syncPlanParameters.extend({
    // Include subbox_rb_export.xml — in the zip alongside the tracks, or as the
    // whole download when includeTracks is false.
    includeRekordboxXml: z.boolean().optional(),
    // False for a metadata-only (XML) download.
    includeTracks: z.boolean().optional(),
    // Where the user will keep the tracks, so the XML's Locations resolve.
    user_root: z.string().optional(),
});

const syncTracksParameters = z.object({
    tracksToDownload: z.array(track),
});

// --- Wishlist types ---

const wishlistStatus = z.enum(['inbox', 'wishlist', 'downloaded', 'available', 'ignored']);

const wishlistItem = z.object({
    album: z.string().nullable().optional(),
    artist: z.string().nullable(),
    bandcamp_url: z.string().nullable().optional(),
    created_at: z.number().nullable().optional(),
    linked_subbox_id: z.string().nullable().optional(),
    metadata_source: z.enum(['auto', 'user']).optional(),
    raw_note: z.string().nullable().optional(),
    resolve_state: z.enum(['pending', 'resolved', 'nomatch']).optional(),
    soundcloud_url: z.string().nullable().optional(),
    status: wishlistStatus,
    title: z.string().nullable(),
    updated_at: z.number().nullable().optional(),
    user_id: z.string(),
    wishlist_id: z.string(),
    youtube_url: z.string().nullable().optional(),
    youtube_video_id: z.string().nullable().optional(),
});

const wishlistList = z.object({
    items: z.array(wishlistItem),
});

const wishlistItemResponse = z.object({
    item: wishlistItem,
});

const wishlistDeleteResponse = z.object({
    success: z.boolean(),
});

const youtubeMatch = z.object({
    confidence: z.number(),
    youtube_title: z.string(),
    youtube_url: z.string(),
    youtube_video_id: z.string(),
});

const matchYoutubeResponse = z.object({
    item: wishlistItem,
    matches: z.array(youtubeMatch),
});

const matchSource = z.enum(['structured', 'musicbrainz', 'string']);

const linkTrackMetadata = z.object({
    album: z.string().nullable().optional(),
    artist: z.string(),
    bandcamp_url: z.string().nullable().optional(),
    confidence: z.number().nullable().optional(),
    is_collection: z.literal(false),
    match_source: matchSource.optional(),
    soundcloud_url: z.string().nullable().optional(),
    source: z.enum(['youtube', 'bandcamp', 'soundcloud']),
    title: z.string(),
    youtube_url: z.string().nullable().optional(),
    youtube_video_id: z.string().nullable().optional(),
});

const musicBrainzMatch = z.object({
    album: z.string().nullable().optional(),
    artist: z.string(),
    score: z.number(),
    title: z.string(),
});

const matchMetadataResponse = z.object({
    match: musicBrainzMatch.nullable(),
});

const wishlistMatchMetadataParameters = z.object({
    artist: z.string().optional(),
    query: z.string().optional(),
    title: z.string().optional(),
});

const linkCollectionMetadata = z.object({
    is_collection: z.literal(true),
    source: z.enum(['youtube', 'bandcamp', 'soundcloud']),
    tracks: z.array(linkTrackMetadata),
});

const linkMetadata = z.discriminatedUnion('is_collection', [
    linkTrackMetadata,
    linkCollectionMetadata,
]);

const parseLinkResponse = z.object({
    metadata: linkMetadata,
});

const wishlistParseLinkParameters = z.object({
    url: z.string(),
});

const wishlistCreateParameters = z.object({
    album: z.string().optional(),
    artist: z.string().optional(),
    bandcamp_url: z.string().optional(),
    soundcloud_url: z.string().optional(),
    title: z.string().optional(),
    youtube_url: z.string().optional(),
    youtube_video_id: z.string().optional(),
});

const wishlistBulkCreateParameters = z.object({
    items: z.array(wishlistCreateParameters),
});

const wishlistBulkCreateResponse = z.object({
    items: z.array(wishlistItem),
});

const wishlistSetSheetParameters = z.object({
    sheet_id: z.string(),
});

const wishlistSetSheetResponse = z.object({
    success: z.boolean(),
});

const wishlistSheetStatusResponse = z.object({
    configured: z.boolean(),
    error: z.string().nullable(),
    sheet_url: z.string().nullable(),
    status: z.enum(['ok', 'error']).nullable(),
});

const wishlistUpdateParameters = z.object({
    album: z.string().optional(),
    artist: z.string().optional(),
    bandcamp_url: z.string().nullable().optional(),
    linked_subbox_id: z.string().optional(),
    status: wishlistStatus.optional(),
    title: z.string().optional(),
    youtube_url: z.string().nullable().optional(),
    youtube_video_id: z.string().nullable().optional(),
});

// --- Playlist tree (subbox-app#147, pymix#201) ---

// One live node of the user's playlist tree, in tree order (depth-first, siblings by
// position). `name` is the node's own, a playlist's leaf too, never a path (pymix#229).
// Null only from a pymix before #229, for a playlist Navidrome no longer has.
const playlistTreeNode = z.object({
    child_count: z.number(),
    kind: z.enum(['folder', 'playlist']),
    name: z.string().nullable(),
    navidrome_playlist_id: z.string().nullable(),
    node_id: z.string(),
    parent_id: z.string().nullable(),
    position: z.number(),
});

const playlistTree = z.object({
    // Navidrome ids of playlists in the user's trash: kept in Navidrome, left out of
    // every list (#150).
    hidden_playlist_ids: z.array(z.string()),
    nodes: z.array(playlistTreeNode),
    // How Navidrome names the user's playlists (pymix#229): 'path' means a name read
    // from getPlaylists already carries its folders (`House / Deep`). Absent from a
    // pymix before #229, which is 'leaf'.
    playlist_names: z.enum(['leaf', 'path']).optional(),
});

export type PlaylistTree = z.infer<typeof playlistTree>;
export type PlaylistTreeNode = z.infer<typeof playlistTreeNode>;

// The tree's write routes (#148, pymix#206). Each answers with the node it wrote, as the
// tree has it minus `child_count`. A playlist's `name` is its leaf (pymix#229); a pymix
// before #229 answered null from PATCH.
const playlistNodeWritten = playlistTreeNode.omit({ child_count: true });

const createPlaylistFolderParameters = z.object({
    name: z.string(),
    parent_id: z.string().nullable().optional(),
    position: z.number().optional(),
});

const createPlaylistInFolderParameters = z.object({
    name: z.string(),
    parent_id: z.string().nullable().optional(),
    song_ids: z.array(z.string()).optional(),
});

// `parent_id` absent keeps the node's parent; null moves it to the root. `position` is
// where it ends up among its new siblings, not counting itself.
const updatePlaylistNodeParameters = z.object({
    name: z.string().optional(),
    parent_id: z.string().nullable().optional(),
    position: z.number().optional(),
});

export type PlaylistNodeWritten = z.infer<typeof playlistNodeWritten>;
export type UpdatePlaylistNodeBody = z.infer<typeof updatePlaylistNodeParameters>;

// --- Trash (subbox-app#150, pymix#200, #207) ---

// Playlists and folders, each with everything under it, into one trash batch. Nothing
// leaves Navidrome: the playlists are hidden until the batch is purged.
const deletePlaylistNodesParameters = z.object({
    node_ids: z.array(z.string()),
});

const playlistNodesDeleted = z.object({
    deleted: z.object({
        folders: z.number(),
        node_ids: z.array(z.string()),
        playlists: z.number(),
    }),
    // "Folder House · 6 playlists", "Playlist Deep", "2 folders · 5 playlists".
    label: z.string(),
    trash_batch_id: z.string(),
});

// One thing a restore wants the user to know, already worded by pymix.
const trashRestoreNote = z.object({
    message: z.string(),
    name: z.string().nullable(),
    node_id: z.string().nullable(),
});

// A `nodes` batch restores synchronously and says what didn't come back as it went:
// `moved` (its folder was deleted separately), `shrunk` (a track was purged while it was
// hidden), `lost` (deleted outside subbox meanwhile). A track batch answers with a
// `job_id` instead (#151).
// A re-import's batch (#151) restores synchronously too: each playlist rewritten to
// what it was before the import, `edits_discarded` when it had changed since.
// `not_restored` names entries whose track can't be found any more.
const reimportRestored = z.object({
    edits_discarded: z.boolean(),
    n_entries: z.number(),
    name: z.string(),
    playlist_id: z.string(),
});

const reimportNotRestored = z.object({ name: z.string(), reason: z.string() });

const trashRestored = z.object({
    batch_id: z.string().optional(),
    failed: z.array(reimportNotRestored).optional(),
    job_id: z.string().optional(),
    lost: z.array(trashRestoreNote).optional(),
    moved: z.array(trashRestoreNote).optional(),
    // A track batch: the restore is a job (#152).
    n_tracks: z.number().optional(),
    not_restored: z.array(z.object({ playlist: z.string() })).optional(),
    restored: z.array(z.union([reimportRestored, z.object({ node_id: z.string() })])).optional(),
    shrunk: z.array(trashRestoreNote).optional(),
    success: z.boolean(),
});

const trashRestoreProgressParameters = z.object({ job_id: z.string() });

// A track restore job (pymix#209): its passes are `checking` and `restoring_files`.
// `warnings` names each track that came back without something it had.
const trashRestoreProgress = z.object({
    in_progress: z.boolean(),
    phase: z.string().nullish(),
    phase_n_processed: z.number().optional(),
    phase_n_total: z.number().optional(),
    phases: z.array(jobPhase).nullish(),
    reason: z.string(),
    result: z.boolean().nullish(),
    warnings: z.string().nullish(),
});

const trashBatch = z.object({
    batch_id: z.string(),
    bytes: z.number(),
    deleted_at: z.number(),
    expires_at: z.number(),
    // For a re-import's batch, `playlist_name` says which playlist each item is.
    items: z.array(z.object({ playlist_name: z.string().nullish() })).optional(),
    kind: z.enum(['nodes', 'playlist_entries', 'track']),
    label: z.string(),
    state: z.string(),
});

const trashList = z.object({
    batches: z.array(trashBatch),
    trash_bytes: z.number(),
});

const trashPurged = z.object({
    errors: z.array(z.unknown()),
    n_purged: z.number(),
    success: z.boolean(),
});

// --- One track's DJ data (subbox-app#237, pymix#269) ---
//
// `cuedata` as GET /track/{id}/dj serves it: `cues` and `loops` hold only pads (index
// 0-7), every memory mark is in `memory`, positions are milliseconds of DJ-app time
// (never decode time: convert through features/dj/utils/dj-offset before drawing or
// seeking). Passthrough throughout, so fields pymix adds later survive a read.

const djOrigin = z.string().nullish();

const djCue = z
    .object({
        color: z.string().nullish(),
        index: z.number(),
        name: z.string().nullish(),
        origin: djOrigin,
        position: z.number(),
    })
    .passthrough();

const djLoop = z
    .object({
        active: z.boolean().nullish(),
        color: z.string().nullish(),
        end: z.number(),
        index: z.number(),
        name: z.string().nullish(),
        origin: djOrigin,
        start: z.number(),
    })
    .passthrough();

const djMemoryMark = z
    .object({
        color: z.string().nullish(),
        end: z.number().nullish(),
        name: z.string().nullish(),
        origin: djOrigin,
        position: z.number(),
        type: z.enum(['cue', 'loop']),
    })
    .passthrough();

// One anchor. Serato-shaped grids carry `beats_till_next` on every anchor but the
// last, which carries `bpm`; Rekordbox-shaped grids carry `bpm` on every anchor.
// `metro` and `battito` (beat of the bar the anchor falls on) are Rekordbox's.
const djGridAnchor = z
    .object({
        battito: z.number().nullish(),
        beats_till_next: z.number().nullish(),
        bpm: z.number().nullish(),
        metro: z.string().nullish(),
        position_ms: z.number(),
    })
    .passthrough();

const djCuedata = z
    .object({
        beatgrid: z.array(djGridAnchor).nullish(),
        beatgrid_meta: z
            .object({ locked: z.boolean().nullish(), origin: djOrigin })
            .passthrough()
            .nullish(),
        bpm: z.number().nullish(),
        cues: z.array(djCue),
        // The DJ app whose time the track's subbox-made positions are in (#241,
        // pymix#291): set while any edited position exists.
        edit_clock: z.enum(['rekordbox', 'serato']).nullish(),
        // What the last import was sent, when it left a subbox edit alone (pymix#271).
        import_incoming: z
            .object({
                cuedata: z.record(z.string(), z.unknown()),
                fields: z.array(z.string()),
                source_app: z.string(),
            })
            .nullish(),
        key: z.string().nullish(),
        loops: z.array(djLoop),
        memory: z.array(djMemoryMark).nullish(),
    })
    .passthrough();

const djGridState = z.enum(['none', 'app', 'edited', 'locked']);

const djTrack = z.object({
    cuedata: djCuedata,
    // Typed by the On export panel (#243).
    export_preview: z.unknown(),
    grid_state: djGridState,
    key: z.string().nullish(),
    source_app: z.string().nullish(),
    subbox_id: z.string(),
    // False when Navidrome couldn't be asked, so a null key means "unknown".
    tags_read: z.boolean(),
    updated_at: z.number().nullish(),
    // What an edit sends back as `expected_version`; 0 when there's no DJ data yet.
    version: z.number(),
});

// Edits (#241, pymix#270): exactly one of cue, loop, grid and key per PATCH, made
// against `expected_version`. A slot's `value` null clears it; `from_slot` moves.
const djSlotEdit = z.object({
    from_slot: z.number().optional(),
    slot: z.number(),
    value: z.record(z.string(), z.unknown()).nullable().optional(),
});

const djPatchParameters = z.object({
    cue: djSlotEdit.optional(),
    expected_version: z.number(),
    grid: z
        .object({
            beatgrid: z.array(z.record(z.string(), z.unknown())).optional(),
            locked: z.boolean().optional(),
        })
        .optional(),
    key: z.object({ value: z.string().nullable() }).optional(),
    loop: djSlotEdit.optional(),
});

const djRevertParameters = z.object({ expected_version: z.number(), to_version: z.number() });

// 409: the track moved on; `current` is it now.
const djConflict = z.object({ current: djTrack, detail: z.string() });

const djHistory = z.object({
    next_before: z.number().nullish(),
    versions: z.array(
        z.object({
            change_type: z.string().nullish(),
            changed_at: z.number().nullish(),
            cuedata: z.record(z.string(), z.unknown()).nullish(),
            // `cue:N`, `loop:N`, `memory`, `grid`, `grid.locked`, `bpm`, `key`.
            fields: z.array(z.string()),
            source_app: z.string().nullish(),
            version: z.number(),
        }),
    ),
});
// The song table's DJ columns for a window of rows (#240, pymix#269). Ids that
// aren't in the user's library are left out of `tracks`.
const djPad = z.object({ color: z.string().nullish(), slot: z.number() });

const djTrackSummary = z.object({
    cue_count: z.number(),
    // Which of pads A-H (0-7) hold a hot cue, and which a loop, in pad order.
    cue_pads: z.array(djPad),
    grid_state: djGridState,
    key: z.string().nullish(),
    loop_count: z.number(),
    loop_pads: z.array(djPad),
    memory_count: z.number(),
    source_app: z.string().nullish(),
});

const djSummary = z.object({
    // False when Navidrome couldn't be asked, so every null key means "unknown".
    tags_read: z.boolean(),
    tracks: z.record(z.string(), djTrackSummary),
});

const djSummaryParameters = z.object({ subbox_ids: z.array(z.string()) });

export type DjCue = z.infer<typeof djCue>;
export type DjCuedata = z.infer<typeof djCuedata>;
export type DjGridAnchor = z.infer<typeof djGridAnchor>;
export type DjGridState = z.infer<typeof djGridState>;
export type DjHistory = z.infer<typeof djHistory>;
export type DjLoop = z.infer<typeof djLoop>;
export type DjMemoryMark = z.infer<typeof djMemoryMark>;
export type DjPatch = z.infer<typeof djPatchParameters>;
export type DjSummary = z.infer<typeof djSummary>;
export type DjTrack = z.infer<typeof djTrack>;
export type DjTrackSummary = z.infer<typeof djTrackSummary>;

// --- The user's own settings (subbox-app#236, pymix#268) ---

// design-dj-ui §5.7: DJ mode hides or shows the DJ surfaces, and nothing else reads it.
// `dj_mode_offered` is set once the app has offered DJ mode, so it is offered only once.
const userSettings = z.object({
    dj_mode: z.boolean(),
    dj_mode_offered: z.boolean(),
});

// Only the settings named are changed (pymix 422s an unknown field), so two devices
// each changing one don't undo each other.
const updateUserSettingsParameters = userSettings.partial();

export type PlaylistNodesDeleted = z.infer<typeof playlistNodesDeleted>;

export type TrashBatch = z.infer<typeof trashBatch>;
export type TrashList = z.infer<typeof trashList>;
export type TrashRestored = z.infer<typeof trashRestored>;
export type TrashRestoreProgress = z.infer<typeof trashRestoreProgress>;
export type UserSettings = z.infer<typeof userSettings>;

export const pymixType = {
    _parameters: {
        create: createParameters,
        createPlaylistFolder: createPlaylistFolderParameters,
        createPlaylistInFolder: createPlaylistInFolderParameters,
        deleteDuplicates: deleteParameters,
        deletePlaylistNodes: deletePlaylistNodesParameters,
        deleteSong: deleteSongParameters,
        djPatch: djPatchParameters,
        djRevert: djRevertParameters,
        djSummary: djSummaryParameters,
        download: downloadParameters,
        exportJob: rbExportParameters,
        import: importParameters,
        importProgress: importProgressParameters,
        inviteRequest: inviteRequestParameters,
        isValidToken: isValidTokenParameters,
        login: loginParameters,
        matchTracks: matchTracksParameters,
        rbImport: rbImportParameters,
        seratoExport: seratoExportParameters,
        seratoImport: seratoImportParameters,
        storageCheck: storageCheckParameters,
        sync: syncParameters,
        syncPlan: syncPlanParameters,
        syncPlaylists: syncPlaylistsParameters,
        syncTracks: syncTracksParameters,
        trashRestoreProgress: trashRestoreProgressParameters,
        updatePlaylistNode: updatePlaylistNodeParameters,
        updateUserSettings: updateUserSettingsParameters,
        wishlistBulkCreate: wishlistBulkCreateParameters,
        wishlistCreate: wishlistCreateParameters,
        wishlistMatchMetadata: wishlistMatchMetadataParameters,
        wishlistParseLink: wishlistParseLinkParameters,
        wishlistSetSheet: wishlistSetSheetParameters,
        wishlistUpdate: wishlistUpdateParameters,
    },
    _response: {
        beetsImportProgress,
        create,
        deleteDuplicates,
        deleteSong,
        djConflict,
        djHistory,
        djSummary,
        djTrack,
        download,
        error,
        exportJob,
        importJob,
        inviteRequest,
        isValidToken,
        librarySize,
        login,
        matchMetadataResponse,
        matchTracks,
        matchYoutubeResponse,
        parseLinkResponse,
        playlistNodesDeleted,
        playlistNodeWritten,
        playlistTree,
        seratoExport,
        storageCheck,
        sync,
        syncPlan,
        syncPlaylists,
        syncTracks: syncPlaylists,
        trashList,
        trashPurged,
        trashRestored,
        trashRestoreProgress,
        userSettings,
        wishlistBulkCreateResponse,
        wishlistDeleteResponse,
        wishlistItem,
        wishlistItemResponse,
        wishlistList,
        wishlistSetSheetResponse,
        wishlistSheetStatusResponse,
        youtubeMatch,
    },
};
